/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import { useControlledState } from '@react-stately/utils';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import * as React from 'react';

import {
    AutoComplete,
    type AutoCompleteProps,
    Badge,
    Heading,
    MENU_MAX_HEIGHT,
    Menu,
    MenuItem,
    MenuItemTitlePrefix,
    MenuPlaceholder,
    MenuRoot,
    MenuSearch,
    MenuSection,
    type Placement,
    Popover,
    Separator,
    SubMenu,
} from '@gitbook/spine-react';
import { usePersistentValue } from '@gitbook/util-react-hooks';

import { typeQueryInPalette, updatePaletteCheckboxCommandSelected } from '../transforms';
import {
    type ComputedCommandsSection,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteDef,
    type PaletteGroupDef,
    type PalettePositionOptions,
    type VisiblePalette,
} from '../types';
import { usePersistentAnchorRef } from '../usePersistentAnchorRef';
import { readCommandsGroup } from './readCommandsGroup';

/**
 * Menu root that accepts a palette.
 */
export function PaletteMenuRoot(props: {
    visiblePalette: VisiblePalette | null;
    onVisiblePaletteChange: React.Dispatch<React.SetStateAction<VisiblePalette | null>>;
}) {
    const { onVisiblePaletteChange } = props;
    const isOpen = Boolean(
        props.visiblePalette && isVisiblePaletteAnchorConnected(props.visiblePalette)
    );
    const visiblePalette = usePersistentValue(props.visiblePalette);
    const initialPosition = useInitialPalettePosition(visiblePalette);
    // Position against a frozen mirror of the anchor so the palette stays pinned to its last
    // location while its (often ephemeral) anchor is removed from the DOM during the close animation.
    const triggerRef = usePersistentAnchorRef(visiblePalette?.anchorRef ?? null);

    React.useLayoutEffect(() => {
        if (props.visiblePalette && !isVisiblePaletteAnchorConnected(props.visiblePalette)) {
            onVisiblePaletteChange(null);
        }
    }, [props.visiblePalette, onVisiblePaletteChange]);

    if (!visiblePalette) {
        return null;
    }
    const { palette } = visiblePalette;

    return (
        <MenuRoot
            key={visiblePalette.id}
            isOpen={isOpen}
            onOpenChange={(isOpen) => {
                if (!isOpen) {
                    onVisiblePaletteChange((current) =>
                        current?.id === visiblePalette.id ? null : current
                    );
                }
            }}
            triggerRef={triggerRef}
        >
            <Popover
                // Mark the palette as a palette overlay so overlays below it (e.g. an inline
                // edit popover whose field opened this palette) don't treat a click inside it
                // as an outside interaction and dismiss themselves before the pick is applied.
                data-palette-overlay=""
                aria-hidden={!isOpen || undefined}
                className={clsx(
                    palette.constrainHeight && 'flex flex-col',
                    !isOpen && 'pointer-events-none'
                )}
                restoreFocus={palette.restoreFocus ?? !visiblePalette.inputRef}
                placement={getPlacementFromPaletteDef(palette)}
                stablePosition
                // When the palette opts in (`constrainHeight`), cap it to the available space on
                // its side so a long list scrolls internally instead of overflowing and being
                // repositioned over the anchor (for input-anchored palettes, the search field).
                resize={palette.constrainHeight ? { maxHeight: MENU_MAX_HEIGHT } : undefined}
            >
                <PaletteAutoCompleteMenu
                    palette={palette}
                    onPaletteChange={(palette) => {
                        onVisiblePaletteChange((current) => {
                            if (!current || current.id !== visiblePalette.id) {
                                return current;
                            }

                            return {
                                ...current,
                                palette: {
                                    ...palette,
                                    position: initialPosition ?? undefined,
                                },
                            };
                        });
                    }}
                    onPaletteClose={() => onVisiblePaletteChange(null)}
                    inputRef={visiblePalette.inputRef}
                />
            </Popover>
        </MenuRoot>
    );
}

/**
 * Check whether a visible palette can still be anchored in the document.
 */
function isVisiblePaletteAnchorConnected(visiblePalette: VisiblePalette | null): boolean {
    return Boolean(visiblePalette?.anchorRef.current?.isConnected);
}

/**
 * Get the initial palette position when the palette is first shown.
 * It avoids resetting the position of the palette when there is a palette self-update.
 */
function useInitialPalettePosition(visiblePalette: VisiblePalette | null) {
    const initialPositionRef = React.useRef<PalettePositionOptions | null>(null);
    React.useEffect(() => {
        if (visiblePalette) {
            initialPositionRef.current = visiblePalette.palette.position ?? null;
        } else {
            initialPositionRef.current = null;
        }
    }, [visiblePalette]);
    return initialPositionRef.current ?? visiblePalette?.palette.position ?? null;
}

function Boundaries(props: { children: React.ReactNode }) {
    return (
        <Sentry.ErrorBoundary
            fallback={<MenuPlaceholder>Error while loading actions</MenuPlaceholder>}
        >
            <React.Suspense
                fallback={
                    <MenuPlaceholder data-spine-popover-stable-position-pending="">
                        Loading…
                    </MenuPlaceholder>
                }
            >
                {props.children}
            </React.Suspense>
        </Sentry.ErrorBoundary>
    );
}

/**
 * We don't use a component to avoid breaking the reading of the tree.
 */
function wrapAutoComplete(
    props: {
        search: PaletteDef['search'] | undefined;
        queryText: string;
        onQueryTextChange?: (query: string) => void;
        isPending?: boolean;
    } & Pick<AutoCompleteProps, 'inputRef' | 'children' | 'filterValue'>
) {
    const { queryText, search, inputRef, onQueryTextChange, isPending, filterValue, children } =
        props;

    if (queryText || search || inputRef) {
        return (
            <AutoComplete
                defaultValue={search?.initialValue}
                filterValue={filterValue}
                value={queryText}
                onValueChange={onQueryTextChange}
                inputRef={inputRef}
            >
                {/* An external `inputRef` (e.g. the editor drives an inline palette) is the
                    query source, so drilled-in descendants must not render their own search
                    field — two inputs for one query leaves an unfocused, broken field. */}
                {search && !inputRef && (
                    <MenuSearch isPending={isPending} label={search?.placeholder} />
                )}
                {children}
            </AutoComplete>
        );
    }

    return children;
}

type PaletteChangeCallback = (palette: PaletteDef) => void;

/**
 * Number of recent queries whose computed sections are kept.
 *
 * Each retained query holds one command list per group, and a content palette's list can run to
 * thousands of commands. Keeping every query typed during one palette session grew the retained
 * set with every keystroke; a handful is enough to still serve the queries a user backspaces
 * back through.
 */
const PALETTE_MENU_CACHE_MAX_QUERIES = 8;

class PaletteMenuCache {
    #cache: Map<string, Map<PaletteGroupDef, ComputedCommandsSection | null>> = new Map();
    #requestIds = new WeakMap<PaletteDef, Map<PaletteGroupDef, number>>();
    #latest = new WeakMap<
        PaletteGroupDef,
        { requestId: number; section: ComputedCommandsSection | null }
    >();
    #nextRequestId = 0;

    get(props: {
        palette: PaletteDef;
        group: PaletteGroupDef;
    }): ComputedCommandsSection | null | undefined {
        const { palette, group } = props;
        const groupsCache = this.#cache.get(palette.query.text);
        if (!groupsCache) {
            return undefined;
        }
        return groupsCache.get(group);
    }
    set(
        props: { palette: PaletteDef; group: PaletteGroupDef },
        value: ComputedCommandsSection | null
    ): void {
        const { palette, group } = props;
        let groupsCache = this.#cache.get(palette.query.text);
        if (!groupsCache) {
            groupsCache = new Map();
            this.#cache.set(palette.query.text, groupsCache);
            // Maps iterate in insertion order, so the first key is the least recently added query.
            while (this.#cache.size > PALETTE_MENU_CACHE_MAX_QUERIES) {
                const oldestQuery = this.#cache.keys().next();
                if (oldestQuery.done) {
                    break;
                }
                this.#cache.delete(oldestQuery.value);
            }
        }
        groupsCache.set(group, value);
    }

    getLatest(group: PaletteGroupDef): ComputedCommandsSection | null | undefined {
        return this.#latest.get(group)?.section;
    }

    getRequestId(props: { palette: PaletteDef; group: PaletteGroupDef }): number {
        const { palette, group } = props;
        let groupRequests = this.#requestIds.get(palette);
        if (!groupRequests) {
            groupRequests = new Map();
            this.#requestIds.set(palette, groupRequests);
        }

        let requestId = groupRequests.get(group);
        if (requestId === undefined) {
            requestId = ++this.#nextRequestId;
            groupRequests.set(group, requestId);
        }
        return requestId;
    }

    setLatest(props: {
        group: PaletteGroupDef;
        requestId: number;
        section: ComputedCommandsSection | null;
    }): void {
        const current = this.#latest.get(props.group);
        if (current && current.requestId > props.requestId) {
            return;
        }
        this.#latest.set(props.group, {
            requestId: props.requestId,
            section: props.section,
        });
    }
}

/**
 * Resolve one function-backed group outside Menu's strict child scanner. Once the reader resolves,
 * it stores concrete menu data and asks the parent to render a scan-able section.
 */
const PaletteGroupSuspenseReader = React.memo(function PaletteGroupSuspenseReader(props: {
    palette: PaletteDef;
    group: PaletteGroupDef;
    cache: PaletteMenuCache;
    requestId: number;
    onResolved: () => void;
}) {
    const section = readCommandsGroup(props.palette, props.group);

    React.useEffect(() => {
        props.cache.set({ palette: props.palette, group: props.group }, section ?? null);
        props.cache.setLatest({
            group: props.group,
            requestId: props.requestId,
            section: section ?? null,
        });
        props.onResolved();
    }, [props.cache, props.group, props.onResolved, props.palette, props.requestId, section]);

    return null;
});

function PaletteGroupSuspense(props: React.ComponentProps<typeof PaletteGroupSuspenseReader>) {
    return (
        <React.Suspense fallback={null}>
            <PaletteGroupSuspenseReader {...props} />
        </React.Suspense>
    );
}

const PaletteAutoCompleteMenu = React.memo(function PaletteAutoCompleteMenuMemo(
    props: {
        defaultPalette?: PaletteDef;
        palette?: PaletteDef;
        onPaletteChange?: (palette: PaletteDef) => void;
        onPaletteClose?: () => void;
        cache?: PaletteMenuCache;
    } & Pick<AutoCompleteProps, 'inputRef'>
) {
    const {
        palette: paletteProp,
        onPaletteChange,
        onPaletteClose,
        defaultPalette: defaultPaletteProp,
        cache: cacheProp,
        inputRef,
    } = props;
    const defaultPalette = defaultPaletteProp ?? paletteProp;
    if (!defaultPalette) {
        throw new Error(`Either "defaultPalette" or "palette" should be filled`);
    }
    const [palette, setPalette] = useControlledState(paletteProp, defaultPalette, onPaletteChange);
    const deferredPalette = React.useDeferredValue(palette);
    const [cache] = React.useState<PaletteMenuCache>(() => cacheProp ?? new PaletteMenuCache());
    return wrapAutoComplete({
        isPending: deferredPalette !== palette,
        search: deferredPalette.search,
        queryText: palette.query.text,
        onQueryTextChange: (query) => setPalette(typeQueryInPalette(palette, query)),
        inputRef,
        filterValue: deferredPalette.query.text,
        children: (
            <Boundaries>
                <PaletteMenu
                    palette={deferredPalette}
                    onPaletteChange={setPalette}
                    onPaletteClose={onPaletteClose}
                    cache={cache}
                />
            </Boundaries>
        ),
    });
});

function PaletteMenu(props: {
    palette: PaletteDef;
    onPaletteChange: (palette: PaletteDef) => void;
    onPaletteClose?: () => void;
    cache: PaletteMenuCache;
}) {
    const [, refresh] = React.useReducer((count: number) => count + 1, 0);
    const onGroupResolved = React.useCallback(() => {
        React.startTransition(refresh);
    }, []);
    const { sections, readers, loading } = getSectionsFromPalette({
        ...props,
        onGroupResolved,
    });

    return (
        <>
            {readers}
            <Menu
                emptyPlaceholder={loading ? 'Loading…' : props.palette.placeholder}
                noResultsPlaceholder={loading ? 'Loading…' : undefined}
            >
                {sections}
            </Menu>
        </>
    );
}

function getPlacementFromPaletteDef(def: PaletteDef) {
    const preferredPlacement = def.position?.preferredPlacement ?? 'bottom';
    const alignment =
        def.position?.preferredAlignment === 'end' || def.position?.preferredAlignment === 'start'
            ? def.position.preferredAlignment
            : preferredPlacement === 'left' || preferredPlacement === 'right'
              ? 'start'
              : null;
    const placement = `${preferredPlacement}${alignment ? `-${alignment}` : ''}` as Placement;
    return placement;
}

/**
 * Read a single group's current snapshot.
 *
 * Static command arrays are safe to materialize during render. Function-backed groups are read by
 * a sibling Suspense reader, and the resolved section is cached before this tree is scanned.
 */
function resolveGroupSection(args: {
    palette: PaletteDef;
    group: PaletteGroupDef;
    cache: PaletteMenuCache;
    staleSection?: ComputedCommandsSection | null;
}): ComputedCommandsSection | null {
    const { palette, group, cache, staleSection } = args;

    const cached = cache.get({ palette, group });
    if (cached !== undefined) {
        return cached;
    }

    if (typeof group.commands === 'function') {
        return staleSection ?? null;
    }

    // Array-backed groups do not read async data, so they can retain the existing synchronous cache.
    const computed = readCommandsGroup(palette, group);
    const commandsSection = computed ?? null;
    cache.set({ palette, group }, commandsSection);
    return commandsSection;
}

function getSectionsFromPalette(props: {
    palette: PaletteDef;
    onPaletteChange: PaletteChangeCallback;
    onPaletteClose?: () => void;
    cache: PaletteMenuCache;
    onGroupResolved: () => void;
}): { sections: React.ReactNode[]; readers: React.ReactNode[]; loading: boolean } {
    const { palette, onPaletteChange, onPaletteClose, cache, onGroupResolved } = props;
    const readers: React.ReactNode[] = [];
    let loading = false;
    const sections = palette.groups.flatMap((group) => {
        const hasCachedSection = cache.get({ palette, group }) !== undefined;
        const isSuspenseGroup = typeof group.commands === 'function' && !hasCachedSection;
        const staleSection = isSuspenseGroup ? cache.getLatest(group) : undefined;
        const hasStaleSection = staleSection !== undefined;
        const commandsSection = resolveGroupSection({
            palette,
            group,
            cache,
            staleSection,
        });

        if (isSuspenseGroup) {
            loading = true;
            const requestId = cache.getRequestId({ palette, group });
            readers.push(
                <PaletteGroupSuspense
                    key={`${group.key}-${palette.query.text}`}
                    palette={palette}
                    group={group}
                    cache={cache}
                    requestId={requestId}
                    onResolved={onGroupResolved}
                />
            );
        }

        if (!commandsSection) {
            if (isSuspenseGroup && !hasStaleSection) {
                return [
                    <MenuSection key={group.key}>
                        {group.title ? (
                            <Heading key={`${group.key}-heading`}>{group.title}</Heading>
                        ) : null}
                        <MenuPlaceholder key={`${group.key}-loading`}>Loading…</MenuPlaceholder>
                    </MenuSection>,
                    <Separator key={`${group.key}-separator`} />,
                ];
            }
            return [];
        }

        const items = commandsSection.data.map((command) => {
            const item = getMenuItemFromPaletteCommandDef({
                command,
                group,
                palette,
                onPaletteChange,
                onPaletteClose,
            });
            if (command.descendant) {
                const subPalette = command.descendant();
                return (
                    <SubMenu key={`${command.key}-submenu`}>
                        {item}
                        <Popover data-palette-overlay="">
                            {subPalette.static ? (
                                (() => {
                                    const nested = getSectionsFromPalette({
                                        palette: subPalette,
                                        onPaletteChange: () => {
                                            throw new Error(
                                                `Trigger that returns a new palette is unsupported with static palette option`
                                            );
                                        },
                                        cache,
                                        onPaletteClose,
                                        onGroupResolved,
                                    });
                                    const nestedContent = wrapAutoComplete({
                                        search: subPalette.search,
                                        queryText: subPalette.query.text,
                                        children: (
                                            <Menu
                                                emptyPlaceholder={subPalette.placeholder}
                                                noResultsPlaceholder={
                                                    nested.loading ? 'Loading…' : undefined
                                                }
                                            >
                                                {nested.sections}
                                            </Menu>
                                        ),
                                    });
                                    return [
                                        ...nested.readers,
                                        React.isValidElement(nestedContent)
                                            ? React.cloneElement(nestedContent, {
                                                  key: `${command.key}-static-submenu`,
                                              })
                                            : nestedContent,
                                    ];
                                })()
                            ) : (
                                <PaletteAutoCompleteMenu
                                    defaultPalette={subPalette}
                                    onPaletteClose={onPaletteClose}
                                    cache={cache}
                                />
                            )}
                        </Popover>
                    </SubMenu>
                );
            }
            return item;
        });

        if (items.length === 0) {
            return [];
        }

        const separator = <Separator key={`${group.key}-separator`} />;

        return [
            <MenuSection key={group.key}>
                {commandsSection?.group.title ? (
                    <Heading>{commandsSection.group.title}</Heading>
                ) : null}
                {items}
            </MenuSection>,
            separator,
        ];
    });
    if (sections.length > 0 && sections[sections.length - 1].type === Separator) {
        sections.pop();
    }
    return { sections, readers, loading };
}

function getMenuItemFromPaletteCommandDef(args: {
    command: PaletteCommandDef;
    group: PaletteGroupDef;
    palette: PaletteDef;
    onPaletteChange: PaletteChangeCallback;
    onPaletteClose?: () => void;
}) {
    const { command, group, palette, onPaletteChange, onPaletteClose } = args;
    const { trigger } = command;
    const checkboxCloseOnActionEnd =
        command.type === PaletteCommandType.CHECKBOX ? command.closeOnActionEnd : undefined;

    return (
        <MenuItem
            key={command.key}
            icon={command.icon}
            data-testid={command.testId}
            hasIconSibling={command.hasIconSibling}
            checked={!command.suffix ? (command.selected ?? command.enabled) : undefined}
            className={clsx(
                command.upgrade && '[--menu-item-focused-bg:--sp-color-bg-upgrade]',
                command.kind === 'outline' &&
                    'before:top-1 before:border before:border-solid before:border-neutral-subtle data-[focused]:before:border-[var(--neutral-ui-bg)]',
                command.className
            )}
            checkbox={command.type === PaletteCommandType.CHECKBOX}
            suffix={
                command.suffix ? (
                    command.suffix
                ) : command.badge ? (
                    <Badge className="shrink-0" size="xsmall" {...command.badge} />
                ) : null
            }
            disabled={command.disabled}
            subtitle={
                command.subtitle ? (
                    typeof command.subtitle === 'string' ? (
                        command.subtitle
                    ) : (
                        <LazySubtitle subtitle={command.subtitle} />
                    )
                ) : undefined
            }
            truncate={command.truncate}
            keywords={command.keywords}
            filterPriority={command.filterPriority}
            textValue={typeof command.title === 'string' ? command.title : undefined}
            size={command.size}
            keyboardShortcut={'keyboardShortcut' in command ? command.keyboardShortcut : undefined}
            onCheckboxChange={
                command.type === PaletteCommandType.CHECKBOX
                    ? (selected) => {
                          // Reflect the new checkbox state in the palette optimistically.
                          onPaletteChange(
                              updatePaletteCheckboxCommandSelected(
                                  palette,
                                  group,
                                  command.key,
                                  selected
                              )
                          );

                          // Toggling the checkbox runs the command itself (the row press path
                          // is handled separately by `onAction`). The palette closes afterwards
                          // unless the command opts out via `closeOnActionEnd`, which lets
                          // multi-select checkboxes keep the palette open.
                          const closeAfterTrigger = checkboxCloseOnActionEnd !== false;
                          const triggerResult = trigger?.();
                          if (triggerResult instanceof Promise) {
                              if (closeAfterTrigger) {
                                  onPaletteClose?.();
                              }
                              return triggerResult.then((nextPalette) => {
                                  if (nextPalette) {
                                      onPaletteChange(nextPalette);
                                  }
                              });
                          }
                          if (triggerResult) {
                              onPaletteChange(triggerResult);
                          } else if (closeAfterTrigger) {
                              onPaletteClose?.();
                          }
                      }
                    : undefined
            }
            onAction={
                trigger
                    ? (event) => {
                          const triggerResult = trigger();
                          if (triggerResult instanceof Promise) {
                              onPaletteClose?.();
                              return triggerResult.then((palette) => {
                                  if (palette) {
                                      event.preventDefault();
                                      onPaletteChange(palette);
                                  }
                              });
                          } else if (triggerResult) {
                              event.preventDefault();
                              onPaletteChange(triggerResult);
                          } else {
                              onPaletteClose?.();
                          }
                      }
                    : undefined
            }
            titlePrefix={
                command.parentTitles ? formatParentTitles(command.parentTitles) : undefined
            }
        >
            {/* If a badge is passed while a suffix is also defined then we display the badge next to the title. */}
            {command.badge && command.suffix ? (
                <span className="inline-flex items-center gap-1">
                    {command.title}
                    <Badge className="shrink-0" size="xsmall" {...command.badge} />
                </span>
            ) : (
                command.title
            )}
        </MenuItem>
    );
}

/**
 * Format the parent titles provided to a command.
 */
function formatParentTitles(titles: string[]) {
    if (titles.length === 0) {
        return null;
    }

    if (titles.length === 1) {
        return <MenuItemTitlePrefix>{titles[0]}</MenuItemTitlePrefix>;
    }

    return (
        <>
            <MenuItemTitlePrefix>{titles[0]}</MenuItemTitlePrefix>
            <MenuItemTitlePrefix>…</MenuItemTitlePrefix>
        </>
    );
}

function LazySubtitle(props: { subtitle: () => string }) {
    return props.subtitle();
}
